import { useContext, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { PageDescription } from '@/components/page-description';
import { PageTitle } from '@/components/page-title';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ROUTES } from '@/constants/routes';
import { AuthContext } from '@/context/auth-context';
import {
  connectableGatewayServers,
  featuredServers,
} from '@/features/ai-apps/catalog';
import { AI_APPS, canSignIn, recipeFor } from '@/features/ai-apps/recipes';
import type {
  AiAppId,
  AiAppRecipe,
  AiAppsServer,
  ClaudePlan,
} from '@/features/ai-apps/types';
import { troubleshootingFor } from '@/features/mcp-access/generate';
import { useMcpServerCatalog } from '@/hooks/queries/mcp-gateway/use-mcp-server-catalog';
import { cn } from '@/lib/utils';
import { useOrgStore } from '@/stores/org-store';
import { orgPath } from '@/utils/org-path';
import { isOrgAdmin } from '@/utils/org-role';

import { AppPicker } from './app-picker';
import { RecipeView } from './recipe-view';

const isAiApp = (value: string | null): value is AiAppId =>
  AI_APPS.includes(value as AiAppId);

const signsIn = (server: AiAppsServer) =>
  AI_APPS.some((app) => canSignIn(server, app));

const ServerChooser = ({
  servers,
  value,
  onChange,
}: {
  servers: AiAppsServer[];
  value: string;
  onChange: (id: string) => void;
}) => (
  <ToggleGroup
    type='single'
    value={value}
    onValueChange={(next) => next && onChange(next)}
    aria-label='Choose what to connect'
    className='flex-wrap justify-start gap-2'
  >
    {servers.map((server) => (
      <ToggleGroupItem
        key={server.profile.id}
        value={server.profile.id}
        data-testid={`ai-apps-server-${server.profile.id}`}
        className={cn(
          'h-auto flex-col items-start rounded-lg border px-3 py-2 text-left text-sm',
          'data-[state=on]:border-primary data-[state=on]:bg-accent/60 data-[state=on]:ring-1 data-[state=on]:ring-primary',
        )}
      >
        <span className='font-medium'>{server.profile.name}</span>
        <span className='text-xs font-normal text-muted-foreground'>
          {server.featured ? 'Sign in as yourself' : 'Capitol gateway · key'}
        </span>
      </ToggleGroupItem>
    ))}
  </ToggleGroup>
);

const ServerSummary = ({ server }: { server: AiAppsServer }) => (
  <div
    className='flex flex-col gap-2 rounded-xl border bg-card p-5 shadow-xs'
    data-testid='ai-apps-server-summary'
  >
    <div className='flex flex-wrap items-center gap-2'>
      <h2 className='text-lg font-semibold'>{server.profile.name}</h2>
      <Badge variant={signsIn(server) ? 'secondary' : 'outline'}>
        {signsIn(server) ? 'Sign in as yourself, no keys' : 'Needs a key'}
      </Badge>
    </div>
    <p className='text-sm text-muted-foreground'>
      {server.profile.description}
    </p>
    {server.profile.capabilities.length > 0 && (
      <div className='flex flex-wrap gap-1.5'>
        {server.profile.capabilities.map((capability) => (
          <Badge key={capability} variant='outline'>
            {capability}
          </Badge>
        ))}
      </div>
    )}
  </div>
);

export const AiAppsPage = () => {
  const { user } = useContext(AuthContext);
  const { currentOrg } = useOrgStore();
  const [params, setParams] = useSearchParams();
  const [announcement, setAnnouncement] = useState('');
  const recipeRef = useRef<HTMLDivElement>(null);

  const orgId = currentOrg?.orgid;
  const isAdmin = isOrgAdmin(user, currentOrg);
  const featured = useMemo(() => featuredServers(orgId), [orgId]);
  // Every member can read the gateway catalog here; members only see it when
  // the org has no featured connector to lead with.
  const gatewayQuery = useMcpServerCatalog(
    orgId,
    isAdmin || featured.length === 0,
  );
  const servers = useMemo(
    () => [...featured, ...connectableGatewayServers(gatewayQuery.data ?? [])],
    [featured, gatewayQuery.data],
  );

  const requestedApp = params.get('app');
  const app = isAiApp(requestedApp) ? requestedApp : null;
  const claudePlan: ClaudePlan =
    params.get('plan') === 'personal' ? 'personal' : 'team';
  const requestedServer = params.get('server');
  const matchedServer = servers.find(
    (candidate) => candidate.profile.id === requestedServer,
  );
  const server = matchedServer ?? servers[0];

  const pageUrl = `${window.location.origin}${orgPath(ROUTES.ACCOUNT.AI_APPS)}?app=claude`;
  const audience = isAdmin ? 'admin' : 'member';
  const recipes = useMemo(() => {
    if (!server) return null;
    const context = { audience, claudePlan, pageUrl } as const;
    return Object.fromEntries(
      AI_APPS.map((id) => [id, recipeFor(server, id, context)]),
    ) as Record<AiAppId, AiAppRecipe>;
  }, [server, audience, claudePlan, pageUrl]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  };

  const pickApp = (next: AiAppId) => {
    setParam('app', next);
    requestAnimationFrame(() => {
      const element = recipeRef.current;
      if (
        element &&
        element.getBoundingClientRect().top > window.innerHeight - 120
      ) {
        element.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const onCopied = (label: string) =>
    setAnnouncement(
      label
        ? `Copied ${label}`
        : 'Copy failed. Select the text and copy it instead.',
    );

  const headline = featured[0]?.profile.name;
  // A linked ?server= may be a gateway server that hasn't loaded yet; don't
  // show another server's steps in the meantime.
  const loadingGateway =
    gatewayQuery.isLoading &&
    (servers.length === 0 || (Boolean(requestedServer) && !matchedServer));

  let body;
  if (loadingGateway) {
    body = <Skeleton className='h-48 w-full rounded-xl' />;
  } else if (!server || !recipes) {
    body = (
      <div
        className='flex flex-col items-start gap-3 rounded-lg border border-dashed p-6 text-sm text-muted-foreground'
        data-testid='ai-apps-empty'
      >
        {gatewayQuery.isError ? (
          <>
            <p>Couldn&apos;t load this organization&apos;s MCP servers.</p>
            <Button
              variant='outline'
              size='sm'
              onClick={() => void gatewayQuery.refetch()}
              disabled={gatewayQuery.isFetching}
            >
              Try again
            </Button>
          </>
        ) : (
          <>
            <p>
              Nothing in {currentOrg?.name ?? 'this organization'} can be added
              to Claude or ChatGPT yet.
            </p>
            {isAdmin && (
              <Button asChild variant='outline' size='sm'>
                <Link to={orgPath(ROUTES.SETTINGS.MCP_SERVERS)}>
                  Publish an MCP server
                </Link>
              </Button>
            )}
          </>
        )}
      </div>
    );
  } else {
    const recipe = app ? recipes[app] : null;
    body = (
      <>
        <section className='flex flex-col gap-3'>
          {servers.length > 1 && (
            <ServerChooser
              servers={servers}
              value={server.profile.id}
              onChange={(id) => setParam('server', id)}
            />
          )}
          <ServerSummary server={server} />
          {gatewayQuery.isError && featured.length > 0 && (
            <p className='text-xs text-muted-foreground'>
              Couldn&apos;t load the organization&apos;s other MCP servers.
            </p>
          )}
        </section>

        <section aria-labelledby='ai-apps-pick' className='flex flex-col gap-3'>
          <h2 id='ai-apps-pick' className='text-base font-semibold'>
            Which app do you use?
          </h2>
          <AppPicker value={app} recipes={recipes} onChange={pickApp} />
        </section>

        <div ref={recipeRef} className='scroll-mt-4'>
          {recipe ? (
            <RecipeView
              recipe={recipe}
              serverName={server.profile.name}
              claudePlan={claudePlan}
              onClaudePlanChange={(plan) => setParam('plan', plan)}
              onPickApp={pickApp}
              onCopied={onCopied}
            />
          ) : (
            <p className='text-sm text-muted-foreground'>
              Pick your app above to see the steps.
            </p>
          )}
        </div>

        <section aria-labelledby='ai-apps-help'>
          <h2 id='ai-apps-help' className='mb-1 text-base font-semibold'>
            Trouble?
          </h2>
          <Accordion type='multiple'>
            {troubleshootingFor(server.profile).map((item) => (
              <AccordionItem key={item.id} value={item.id}>
                <AccordionTrigger>{item.title}</AccordionTrigger>
                <AccordionContent className='text-sm text-muted-foreground'>
                  {item.body}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </>
    );
  }

  return (
    <div
      className='flex w-full min-w-0 max-w-5xl flex-col gap-8'
      data-testid='ai-apps-page'
    >
      <div>
        <PageTitle title='Add to Claude / ChatGPT' />
        <PageDescription>
          {headline
            ? `Use ${headline} from the AI apps you already use. Pick your app and follow the steps. You sign in as yourself, so there are no keys to copy.`
            : "Connect your organization's MCP servers to the AI apps you already use."}
        </PageDescription>
      </div>
      {body}
      <div className='sr-only' aria-live='polite' data-testid='copy-live'>
        {announcement}
      </div>
    </div>
  );
};
